import { ReactiveVar } from '@apollo/client'

import { globalWindow, localStorage } from '@emico/ssr-utils'
import {
  getStoreViews,
  storeViewChangeListeners,
  findActiveStoreView,
} from '@emico/storeviews'

import { parseStoredVar } from './parseStoredVar'
import { persistReactiveVar } from './persistReactiveVar'

interface SyncReactiveVarConfig<T> {
  prefix?: string
  sharedBetweenSites?: boolean
  defaultValue?: T
  generateWebsitePrefix?: () => string
}

function getWebsitePrefix() {
  const storeViews = getStoreViews()
  const activeStoreView = findActiveStoreView(storeViews)
  return String(activeStoreView?.websiteId)
}

function getStorageKey(
  prefix: string,
  sharedBetweenSites: boolean,
  storageKey: string,
) {
  const websitePrefix = sharedBetweenSites ? false : getWebsitePrefix()

  const combinedStorageKey = [prefix, websitePrefix, storageKey]
    .filter(Boolean)
    .join('/')

  return combinedStorageKey
}

/**
 * Check if there is a stored value, and restore it or set the default value
 */
function restoreStoredValue<T>(
  combinedStorageKey: string,
  rv: ReactiveVar<T>,
  defaultValue: T | undefined,
) {
  const serializedValue = localStorage?.getItem(combinedStorageKey)
  const storedValue = parseStoredVar(serializedValue ?? null)

  if (storedValue !== undefined) {
    rv(storedValue)
  } else if (defaultValue !== undefined) {
    rv(defaultValue)
  }
}

export function syncReactiveVar<T>(
  rv: ReactiveVar<T>,
  storageKey: string,
  config?: SyncReactiveVarConfig<T>,
) {
  const { defaultValue, prefix = '@emico', sharedBetweenSites = false } =
    config ?? {}

  let combinedStorageKey = getStorageKey(prefix, sharedBetweenSites, storageKey)

  restoreStoredValue<T>(combinedStorageKey, rv, defaultValue)

  let removeListener = persistReactiveVar(rv, combinedStorageKey)

  // If the stored value changes, read its value
  const handleStorageEvent = (e: StorageEvent) => {
    if (e.key !== combinedStorageKey) {
      return
    }

    rv(parseStoredVar(e.newValue) ?? defaultValue)
  }

  globalWindow?.addEventListener('storage', handleStorageEvent)

  // When the the (active) storeViews changes the websiteId might de different,
  // thus we need to update the value and start listening/writing to the new
  // storage ID when it is changed.
  storeViewChangeListeners.add(() => {
    removeListener()
    combinedStorageKey = getStorageKey(prefix, sharedBetweenSites, storageKey)
    restoreStoredValue<T>(combinedStorageKey, rv, defaultValue)
    removeListener = persistReactiveVar(rv, combinedStorageKey)
  })

  return rv
}
